import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Coffee,
  Footprints,
  Sparkles,
  Hotel,
  Utensils,
  Landmark,
  Plane,
  AlertTriangle,
  Mountain,
  Waves,
  Car,
  BookOpen,
  ShoppingBag,
  Camera,
} from 'lucide-react';
import ItineraryMap, { buildMapLocations } from './ItineraryMap';
import { galwayCoordinates, galwayCenter } from './coordinates/galway';
import { CoeliacRef, CoeliacAward, CoeliacRefTag, CoeliacAwardTag } from './CoeliacTags';

interface Activity {
  icon: React.ReactNode;
  title: string;
  description: string;
  time?: string;
  image?: string;
  location?: string;
  muted?: boolean;
  coeliacRefs?: CoeliacRef[];
  coeliacSummary?: string;
  coeliacAward?: CoeliacAward;
}

interface Day {
  date: string;
  dayNumber: number;
  title: string;
  subtitle: string;
  theme: 'galway-arrival' | 'galway-event' | 'galway-depart';
  activities: Activity[];
  highlight?: boolean;
}

const galwayItinerary: Day[] = [
  {
    date: 'Sunday, May 3',
    dayNumber: 1,
    title: 'West to Galway',
    subtitle: 'Late drive west after Kilmainham — into Galway for a 9:30 PM Handsome Burger dinner',
    theme: 'galway-arrival',
    activities: [
      {
        icon: <Car size={24} />,
        title: 'Pick up rental car — Dublin Airport',
        description:
          'Car booked for 6:30 PM pickup at Dublin Airport (rebooked from 4:30 PM to fit Kilmainham). Depots are in the multi-storey car park (Avis, Hertz, Enterprise, Europcar all on-site). Allow 30–45 min for paperwork. Request automatic if not already specified — Irish left-side driving is fine but manual on top is a lot. Set GPS for Galway via M50 → M6.',
        location: 'Dublin Airport',
        time: '6:30 PM',
        muted: true,
      },
      {
        icon: <Car size={24} />,
        title: 'Drive Dublin Airport → Galway',
        description:
          'Direct route: M50 south, M4 → M6 west to Galway. ~2h15 with light Sunday-evening traffic. Quick fuel/coffee stop at the Applegreen at Enfield or Athlone services if needed.',
        location: 'Latin Quarter, Galway',
        time: '7:00 PM',
        muted: true,
      },
      {
        icon: <Hotel size={24} />,
        title: 'Arrive Galway — hotel check-in (quick)',
        description:
          'Drop the car at the hotel and walk everywhere for the next two days. Good bases: The House Hotel in the Latin Quarter, or The g Hotel & Spa if you want spa access. Bags drop only — head straight to Handsome Burger. Proper freshen-up after dinner.',
        location: 'Latin Quarter, Galway',
        time: '9:15 PM',
        muted: true,
      },
      {
        icon: <Utensils size={24} />,
        title: 'Handsome Burger — Mainguard Street',
        description:
          "Kai was closed (Sunday hours), so dinner moved to Handsome Burger on Mainguard Street — Galway's stand-out independent burger spot, walking distance from the Latin Quarter hotels. They do a GF bun and have a dedicated coeliac-friendly approach (flag coeliac at the till and they'll confirm fryer separation for fries). Casual, fast, open late — exactly the right move after a long Dublin day. Open until 10 PM Sundays.",
        location: 'Mainguard Street, Galway',
        time: '9:30 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
        coeliacSummary:
          'GF bun available. Confirm fryer arrangements for fries when ordering — historically they handle coeliac orders carefully but cross-contamination on shared fryer is the standard risk. Flag coeliac at the till.',
        coeliacRefs: [
          {
            title: 'Handsome Burger official',
            url: 'https://handsomeburger.ie/',
            summary: 'Independent Galway burger restaurant. GF bun option; flag coeliac to confirm fryer handling.',
          },
        ],
      },
    ],
  },
  {
    date: 'Monday, May 4',
    dayNumber: 2,
    title: 'Galway Bank Holiday — Walking Day, No Driving',
    subtitle:
      'Coffee, Latin Quarter, St Nicholas, Cathedral, Salthill — pack in the city sights, all driving moved to Tuesday',
    theme: 'galway-event',
    highlight: true,
    activities: [
      {
        icon: <AlertTriangle size={24} />,
        title: 'May Day bank holiday — what closes',
        description:
          "Public holiday. Banks are shut (so Lynch's Castle interior is closed today — exterior only). Galway City Museum is closed Mondays year-round including bank holidays. Hooked on Henry Street is also closed Mondays. Cathedral, St Nicholas' Collegiate Church, Galway Market, Charlie Byrne's, the Atlantaquaria, and most restaurants/cafés open as normal — Charlie Byrne's opens late at noon on bank holidays.",
        time: 'Note',
        muted: true,
      },
      {
        icon: <Coffee size={24} />,
        title: 'Branch & Caffe — coffee and cake',
        description:
          "Coffeewerk + Press was shut on the bank holiday, so the morning stop became Branch & Caffe on Mary Street — a short walk north of the Latin Quarter. Coffee plus cake to start the day. Casual, sit-in.",
        location: 'Mary Street, Galway',
        time: '8:30 AM',
        image: '/images/dublin/dublin-coffee.jpg',
      },
      {
        icon: <Landmark size={24} />,
        title: 'Spanish Arch & Long Walk',
        description:
          "The 16th-century Spanish Arch was built into the old city walls so Spanish wine merchants could unload cargo onto the quay. Sit on the riverside steps and watch the Corrib. The Long Walk (the row of Georgian houses opposite, painted in primary colours) is one of Galway's most photographed views. Outdoor, free and always open, so it works on the bank-holiday morning.",
        location: 'Spanish Arch, Galway',
        time: '8:45 AM',
        image: '/images/galway/spanish-arch.jpg',
      },
      {
        icon: <Landmark size={24} />,
        title: "Lynch's Castle (exterior) + Latin Quarter walk",
        description:
          "Shop Street. The best-preserved medieval merchant townhouse in Ireland, built c.1500 by the Lynch family — one of the 14 Tribes of Galway. Today an AIB branch (closed bank holiday, so interior is off-limits), but the exterior carved limestone, gargoyles, and Lynch family crest are the main draw. Walk Shop Street → Quay Street through the Latin Quarter on the way to St Nicholas'.",
        location: 'Shop Street, Galway',
        time: '9:30 AM',
        image: '/images/galway/lynchs-castle.jpg',
      },
      {
        icon: <BookOpen size={24} />,
        title: "St Nicholas' Collegiate Church",
        description:
          'The oldest medieval church in continuous use in Ireland — founded 1320, still an active place of worship. Christopher Columbus reportedly prayed here in 1477 before sailing west. The carved grotesques on the outer walls are worth a close look, and the font inside holds holy water from the River Jordan. **Opens 10:00 AM Monday** — first in for a quiet visit. ~30 min. Donation entry.',
        location: 'Lombard Street, Galway',
        time: '10:00 AM',
        image: '/images/galway/st-nicholas-galway.jpg',
      },
      {
        icon: <ShoppingBag size={24} />,
        title: 'Galway Market — bank holiday opening',
        description:
          "The market wraps around St Nicholas' Collegiate Church on Saturdays, Sundays, and bank holidays — so it's open today. Local producers: artisan bread, smoked fish, cheese, charcuterie, crepes, hot food. A few coeliac-friendly stalls (smoked salmon is a safe naturally-GF buy). Worth a wander even just for the atmosphere.",
        location: 'Lombard Street, Galway',
        time: '10:45 AM',
        image: '/images/galway/galway-market.jpg',
      },
      {
        icon: <Landmark size={24} />,
        title: 'Eyre Square — Browne Doorway & JFK memorial',
        description:
          "Galway's main public square, redesigned in 2006. Browne Doorway is a 17th-century merchant's doorway preserved at the north end. The JFK memorial commemorates Kennedy's June 1963 visit to Galway, just months before his assassination — he was made a Freeman of the city here. The Quincentennial Fountain (Galway Hooker sails) is the centrepiece. 15-min stop on the way back south.",
        location: 'Eyre Square, Galway',
        time: '11:45 AM',
        image: '/images/galway/eyre-square.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'Salmon Weir Bridge & River Corrib',
        description:
          "Walk north from Spanish Arch along the river — past the cathedral and onto the Salmon Weir Bridge. In late spring/early summer the salmon are running upstream and you can lean over the rail and watch them holding in the current below. The cathedral dome and the weir together make one of Galway's signature views.",
        location: 'Gaol Road, Galway',
        time: '2:00 PM',
        image: '/images/galway/salmon-weir-bridge.jpg',
      },
      {
        icon: <Landmark size={24} />,
        title: 'Galway Cathedral',
        description:
          'Built 1965 on the site of the old county jail (Galway Gaol), with a distinctive copper dome visible from across the city. Free entry, open daily ~08:30–18:30 including bank holidays. Marble-mosaic floors, rose window, and a side chapel with a JFK mosaic alongside Patrick Pearse — the only place in Ireland the two are paired. ~30 min visit.',
        location: 'Gaol Road, Galway',
        time: '2:15 PM',
        image: '/images/galway/galway-cathedral.jpg',
      },
      {
        icon: <BookOpen size={24} />,
        title: "Charlie Byrne's Bookshop",
        description:
          "Middle Street. A Galway institution — three rooms of new and secondhand books with a famously knowledgeable staff and an Irish-literature corner that's the best in the city. **Bank-holiday hours: opens 12:00 PM** (regular Monday is 9 AM, but bank holidays late). Easy half-hour browse.",
        location: 'Middle Street, Galway',
        time: '3:00 PM',
      },
      {
        icon: <Waves size={24} />,
        title: 'Salthill Promenade & Blackrock Diving Tower',
        description:
          'Bus from Eyre Square (10 min, ~€2.50 with Leap card) or 35-min walk along the river/coast. The Salthill Prom is a 2 km flat walk along Galway Bay — Atlantic views back across to the Burren hills. Walk all the way to the Blackrock Diving Tower (built 1953, used year-round by sea swimmers) and **kick the wall at the far end for good luck** — local tradition.',
        location: 'Salthill, Galway',
        time: '4:00 PM',
        image: '/images/galway/salthill-promenade.jpg',
      },
      {
        icon: <Footprints size={24} />,
        title: 'Mutton Island Causeway',
        description:
          'From Salthill, walk back east along the coast road (~25 min) — or cut through South Park from the Claddagh — to the entrance of the Mutton Island Causeway. The 850 m paved causeway runs out into Galway Bay to a small island and lighthouse. The island itself is gated off, but the causeway walk is the experience: open sky, mudflats and birdlife at low tide, the city skyline behind you, and the Burren hills across the bay. Free, always open, no booking. ~30–40 min round trip including time at the far end.',
        location: 'Mutton Island Causeway, Galway',
        time: '5:30 PM',
        image: '/images/galway/mutton-island.jpg',
      },
      {
        icon: <Camera size={24} />,
        title: 'Spanish Arch & Long Walk — golden hour',
        description:
          "Loop back through the Claddagh and across to the Spanish Arch for the evening light on the Long Walk — the row of Georgian houses opposite the arch, painted in primary colours, with the Corrib in front. Galway's most photographed view, and properly stunning in the soft late-evening light (sunset is ~9 PM in early May, so anything from 7 PM onwards lights up). Free.",
        location: 'Spanish Arch, Galway',
        time: '6:30 PM',
        image: '/images/galway/long-walk-galway.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Dinner — The Front Door Pub',
        description:
          "Cross Street, right in the Latin Quarter. Traditional Galway pub-restaurant on three floors. Walk-in dinner — flagged coeliac at the table.",
        location: 'Cross Street, Galway',
        time: '7:30 PM',
        image: '/images/dublin/dublin-restaurant.jpg',
      },
    ],
  },
  {
    date: 'Tuesday, May 5',
    dayNumber: 3,
    title: 'Drive Day — Cliffs of Moher, Burren, then Dublin Airport',
    subtitle: 'Early start, all driving today, fit Cliffs + Burren on the way to a 15:15 LHR flight',
    theme: 'galway-depart',
    highlight: true,
    activities: [
      {
        icon: <AlertTriangle size={24} />,
        title: 'Drive day — early start, tight timing',
        description:
          'Galway → Dunguaire → Burren → Cliffs of Moher → Dublin Airport. Total moving time ~5 hours plus stops. **Leave Galway at 7:00 AM** to make the 15:15 flight comfortably. Day-after-bank-holiday traffic returning to Dublin is heavier than usual on the M6/M4/M50 — add a 30-min buffer. Cliffs of Moher requires a **pre-booked timed entry** — book the 9:00 or 9:30 slot online before today (€10 adult peak, €9 off-peak; cheaper before 11:00).',
        time: 'Note',
        muted: true,
      },
      {
        icon: <Hotel size={24} />,
        title: 'Checkout & car pickup',
        description:
          'Pack the night before. Quick hotel checkout, collect car from the hotel car park. No coffee stop today — get a takeaway from the hotel or grab one at the Cliffs visitor centre later. Set GPS for Dunguaire Castle, Kinvara via N67.',
        location: 'Latin Quarter, Galway',
        time: '6:45 AM',
        muted: true,
      },
      {
        icon: <Car size={24} />,
        title: 'Depart Galway — N67 coast road south',
        description:
          'Take the N67 scenic coastal route south, not the motorway — better views, only adds 10 min vs M18 to Kinvara. Light traffic at this hour.',
        location: 'Latin Quarter, Galway',
        time: '7:00 AM',
        muted: true,
      },
      {
        icon: <Landmark size={24} />,
        title: 'Dunguaire Castle, Kinvara',
        description:
          "16th-century tower house perched on the shores of Galway Bay, just outside Kinvara village. One of the most-photographed castles in Ireland. **15-min photo stop** — interior won't be open this early, but the exterior + the bay views are the draw.",
        location: 'Kinvara, Co. Galway',
        time: '8:00 AM',
        image: '/images/galway/dunguaire-castle.jpg',
      },
      {
        icon: <Mountain size={24} />,
        title: 'The Burren — Poulnabrone Dolmen',
        description:
          'Drive the R480 through the heart of the Burren — an otherworldly limestone pavement landscape, utterly unlike anywhere else in Ireland. Early May is peak wildflower season: spring gentians (intense cobalt blue), early-purple orchids, and mountain avens grow in the cracks of the limestone. Stop at the Poulnabrone Dolmen — a 5,000-year-old portal tomb right off the road, free, no booking. **30-min stop** — short walk in, photos, back on the road.',
        location: 'Burren National Park',
        time: '8:30 AM',
        image: '/images/galway/poulnabrone-dolmen.jpg',
      },
      {
        icon: <Mountain size={24} />,
        title: 'Cliffs of Moher — pre-booked entry',
        description:
          "214m sheer sea cliffs over the Atlantic — a UNESCO Global Geopark. Pre-book a 9:00 or 9:30 AM timed entry online (cheaper off-peak and beats the queues). The clifftop path is flat and well-surfaced. Walk south to O'Brien's Tower for the view north along the cliffs — on a clear day you'll see the Aran Islands across the water. **Allow 1 hour 15 — be moving by 10:45 AM.**",
        location: 'Cliffs of Moher',
        time: '9:15 AM',
        image: '/images/galway/cliffs-of-moher.jpg',
      },
      {
        icon: <Utensils size={24} />,
        title: 'Grab-and-go lunch — Cliffs visitor centre',
        description:
          "Sit-down lunch in Lisdoonvarna won't fit if you want a comfortable airport buffer. Grab a coffee + a salad/sandwich at the Cliffs visitor centre café before leaving. GF options are limited but they do mark allergens — naturally-GF salads/soup are the safest bet. Eat in the car or on the road. Alternative: pack a lunch from Galway (Ard Bia or the Saturday Market on Monday).",
        location: 'Cliffs of Moher',
        time: '10:45 AM',
        muted: true,
      },
      {
        icon: <Car size={24} />,
        title: 'Drive Cliffs → Dublin Airport',
        description:
          'N85 → M18 → M6 → M50 → Dublin Airport exit. ~3h00–3h30 mostly motorway, with tolls (M6 + M50). Fuel up at the Applegreen at Lisdoonvarna or Gort on the way out — cheaper than near the airport and most rentals require returning the car full. Day-after-bank-holiday traffic on the M6/M4/M50 approaching Dublin is the main risk; add buffer.',
        location: 'Dublin Airport',
        time: '11:00 AM',
        muted: true,
      },
      {
        icon: <Plane size={24} />,
        title: 'Arrive Dublin Airport — drop car, terminal, fly home',
        description:
          "Target 14:00 at Dublin Airport — that gives 75 min for rental return + security ahead of the 15:15 LHR. Top up fuel at the Circle K on the R132 just before the airport if you didn't fill up earlier. Drop the car at the rental return in the multi-storey, then terminal. Dublin 15:15 → LHR 16:45. Connect to the 20:50 flight via Singapore — landing in SIN 17:30 the following day for the layover.",
        location: 'Dublin Airport',
        time: '2:00 PM',
        image: '/images/dublin/dublin-skyline.jpg',
        muted: true,
      },
    ],
  },
];

const GalwayTab: React.FC = () => {
  return (
    <>
      <motion.section
        className="hero galway-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-background">
          <img src="/images/dublin/dublin-skyline.jpg" alt="Galway Bay" />
          <div className="hero-overlay galway-overlay" />
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Calendar size={16} />
            <span>May 3 – 5, 2026</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Galway
          </motion.h1>
          <motion.div
            className="hero-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="hero-detail">
              <MapPin size={20} />
              <span>Latin Quarter → Cliffs of Moher → Salthill</span>
            </div>
            <div className="hero-detail">
              <Mountain size={20} />
              <span>3-Day West Coast Roadtrip</span>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="scroll-line" />
        </motion.div>
      </motion.section>

      <section className="intro-section galway-intro">
        <div className="intro-content">
          <h2>Galway 2026</h2>
          <p>
            Three days on the Atlantic edge: pick up the rental at Dublin Airport on Sunday afternoon, drive west on the
            M6, and base in the Latin Quarter for two nights. Monday is a full day-trip to the Cliffs of Moher via
            Kinvara and the Burren&apos;s limestone pavement. Tuesday morning covers the Galway sights you didn&apos;t
            reach on arrival, then the direct M6 drive back to Dublin Airport for the flight home.
          </p>
          <p>
            The Cliffs are a day trip rather than an overnight in Doolin — two nights in Galway means time to actually
            explore the Latin Quarter, Salthill, and the Crane Bar for trad music. The
            clifftop walk is flat. Bank holiday Monday means pre-booking timed Cliffs entry is essential.
          </p>
        </div>
      </section>

      <section className="timeline-section">
        <h2 className="section-title galway-section-title">Day by Day</h2>

        <div className="timeline">
          {galwayItinerary.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className={`day-card ${day.theme} ${day.highlight ? 'highlight galway-highlight' : ''}`}
            >
              <div className="day-header">
                <div className="day-number">Day {day.dayNumber}</div>
                <div className="day-info">
                  <div className="day-date">{day.date}</div>
                  <h3 className="day-title">{day.title}</h3>
                  <p className="day-subtitle">{day.subtitle}</p>
                </div>
              </div>

              <div className="activities">
                {day.activities.map((activity, activityIndex) => (
                  <div
                    key={activityIndex}
                    className={`activity ${activity.muted ? 'muted' : ''} ${!activity.image ? 'no-image' : ''}`}
                  >
                    {activity.image && (
                      <div className="activity-image">
                        <img src={activity.image} alt={activity.title} />
                      </div>
                    )}
                    <div className="activity-content">
                      <div className="activity-header">
                        <div className="activity-icon galway-icon">{activity.icon}</div>
                        <div className="activity-header-text">
                          <h4 className="activity-title">{activity.title}</h4>
                          {activity.time && <div className="activity-time galway-time">{activity.time}</div>}
                        </div>
                      </div>
                      {activity.location && (
                        <div className="activity-location">
                          <MapPin size={12} />
                          <span>{activity.location}</span>
                        </div>
                      )}
                      <p className="activity-description">{activity.description}</p>
                      <div className="coeliac-tags-row">
                        {activity.coeliacAward && <CoeliacAwardTag award={activity.coeliacAward} />}
                        {activity.coeliacRefs && activity.coeliacRefs.length > 0 && (
                          <CoeliacRefTag refs={activity.coeliacRefs} summary={activity.coeliacSummary} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ItineraryMap
        locations={buildMapLocations(galwayItinerary, galwayCoordinates)}
        center={galwayCenter}
        zoom={11}
        themeColor="#3a7d8c"
        totalDays={3}
      />

      <section className="intro-section galway-intro">
        <div className="intro-content">
          <h2>Critical Booking Checklist</h2>
          <p>
            <strong>Cliffs of Moher</strong> — Pre-book a timed entry online before the trip. Bank-holiday Monday demand
            is heavy; online tickets are also cheaper than walk-up.
          </p>
          <p>
            <strong>Galway accommodation (Sun 3 – Tue 5 May)</strong> — Two nights. The House Hotel in the Latin Quarter
            is the default pick; The g Hotel &amp; Spa if spa access matters. Bank- holiday weekend so weekend rates
            apply — lock it in early.
          </p>
          <p>
            <strong>Car hire</strong> — Sun 3 May 4:30 PM pickup at Dublin Airport, Tue 5 May drop-off at Dublin
            Airport. Request an automatic. Most rentals require returning the car full — fuel up at Headford Road
            Applegreen on the way out of Galway, or Circle K on the R132 just before Dublin Airport.
          </p>
          <p>
            <strong>Kai Restaurant</strong> — Book ahead and mention coeliac when booking. Sea Road — 10-min walk from
            the Latin Quarter.
          </p>
          <p>
            <strong>Monday-night dinner</strong> — Walked in to The Front Door Pub on Cross Street; flagged coeliac at
            the table.
          </p>
        </div>
      </section>

      <section className="intro-section galway-intro">
        <div className="intro-content">
          <h2>Quick Reference</h2>
          <p>
            <strong>Restaurants:</strong> Kai (Sea Road — strongest coeliac record in Galway, separate GF dessert bar),
            Cava Bodega (Lower Dominick Street — JP McMahon tapas, allergen-marked), Hooked on Henry Street (GF fish
            &amp; chips alternative).
          </p>
          <p>
            <strong>Trad music:</strong> Crane Bar (Sea Road, listening session 9:30 PM), Tigh Coili (Latin Quarter,
            sessions from 6 PM).
          </p>
          <p>
            <strong>Sights:</strong> Spanish Arch &amp; Galway City Museum (free, free), St Nicholas&apos; Collegiate
            Church (oldest in continuous use in Ireland), Galway Cathedral, Eyre Square, Salthill Promenade, Claddagh
            village, Cliffs of Moher (day trip), the Burren &amp; Poulnabrone Dolmen.
          </p>
          <p>
            <strong>GF standouts:</strong> Kai (separate GF dessert bar, strongest coeliac reviews after Glas in
            Dublin). Cava Bodega and the Roadside Tavern are coeliac-aware but not dedicated kitchens — flag firmly and
            avoid fried items at both.
          </p>
        </div>
      </section>

      <motion.footer
        className="footer galway-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={32} />
          <h3>Sláinte from Galway</h3>
          <p>May 3 – 5, 2026</p>
        </div>
      </motion.footer>
    </>
  );
};

export default GalwayTab;
